import _ from 'underscore';
import { useCallback, useReducer } from 'react';

const existDup = (arr, newArr, itemKey) => {
  const keys = _.pluck(arr, itemKey);
  const newKeys = _.pluck(newArr, itemKey);

  return _.intersection(keys, newKeys).length > 0;
};

const ACTION_TYPE_SET_FOLDER_DATA = 100;
const ACTION_TYPE_SET_PERF_TIME_SERIES = 200;
const ACTION_TYPE_RESET_DATA = 300;

const AL_FOLDER_REDUCER_INIT_STATE = {
  folders: null,
  assets: null,
  perfTimeSeriesData: null,
};

/*
  state：{
    // null - not yet initialized
    // [] - no folders
    folders: [
      {
        folderId: id of folder,
        folderName: name of folder,
        parentFolderId: id of parent folder, if parent is root
        parentHierarchy: [],
        assets: [],
        subfolders: [],
        itemCount: {
          ImageAssetsCount,
          LogoAssetsCount,
          SubFoldersCount,
          TextAssetsCount,
          VideoAssetsCount,
          TotalCount,
        },
        createdTime: time folder was created
        modifiedTime: last time folder was modified
      }
    ],
    assets: [
      {
        id,  // "1234"
        name,
        folderAssetType, // "Image"
        url,
        originalUrl,
        sourceType, // "UserUploaded",
        type, // "GenericImage",
        height,
        width,
        originalHeight,
        originalWidth,
        cropSettings,
        cropSettingOnOriginal,
        placementSettings: [
        ],
        createdTime,
        modifiedTime,

        // !! Performance metrics, new for V4
        usedByCount: 10, // ads count?
        performanceLabel, // "Best", "Good", "Low",
        // # performanceMetrics
        impressions,
        clicks,
        CTR,
        spend,
        // # performanceMetricsChange
        impressionsChange,
        clicksChange,
        CTRChange,
        spendChange,
        // # performanceMetricsChangePercentage
        impressionsChangePercentage,
        clicksChangePercentage,
        CTRChangePercentage,
        spendChangePercentage,
        // # periodPerformanceMetrics
        periodImpressions,
        periodClicks,
        periodCTR,
        periodSpend,
      }
    ],
    perfTimeSeriesData: [ // one entry for each selected metric, at most 2 selected metrics on UI chart currently
      {
        metricName: "Impressions",
        uniqueId: 'a guid',
        primaryDataPoints: [
          {
            datetime: "2023-06-12T00:00:00",
            value: 400,
          },
          ...
        ],
        comparisonDataPoints: [ // empty if POP is disabled
          {
            datetime: "2023-06-12T00:00:00",
            value: 600,
          },
          ...
        ],
      },
    ],

    // mark where the data are loaded
    pageName: AdsStudioPageName.LibraryPage/AdsStudioPageName.PerfPage/undefined
  }
*/
const folderDataStateReducer = (state, action) => {
  const { type: actionType, ...actionData } = action;
  switch (actionType) {
    case ACTION_TYPE_SET_FOLDER_DATA:
    {
      const {
        newFolders = [], newAssets = [], newPerfTimeSeries = [], shouldFlush, logger, pageName,
      } = actionData;

      if (shouldFlush) {
        return {
          ...state, folders: newFolders, assets: newAssets, perfTimeSeriesData: newPerfTimeSeries, pageName,
        };
      }

      // todo: investigate why there are duliacted folders and assets
      const foundFolderDup = existDup(state.folders, newFolders, 'folderId');
      const foundAssetDup = existDup(state.assets, newAssets, 'id');
      if (foundFolderDup) {
        logger.logTrace('Unexpected duplicate folders found in folderDataStateReducer');
      }
      if (foundAssetDup) {
        logger.logTrace('Unexpected duplicate assets found in folderDataStateReducer');
      }
      if (foundFolderDup || foundAssetDup) {
        // if found duplicate, which should not happen, don't update the state
        return state;
      }

      // todo: don't update time series data if shouldFlush is false, is that expected? confirm with MT
      return {
        ...state,
        folders: [...(state.folders || []), ...newFolders],
        assets: [...(state.assets || []), ...newAssets],
      };
    }
    case ACTION_TYPE_SET_PERF_TIME_SERIES:
    {
      const { newPerfTimeSeries } = actionData;
      return { ...state, perfTimeSeriesData: newPerfTimeSeries };
    }
    case ACTION_TYPE_RESET_DATA:
    {
      return AL_FOLDER_REDUCER_INIT_STATE;
    }
    default:
      return state;
  }
};

export const useDataServiceReducer = (logger, initState = AL_FOLDER_REDUCER_INIT_STATE) => {
  const [folderData, dispatch] = useReducer(folderDataStateReducer, initState);

  const setFolderData = useCallback(({
    newAssets, newFolders, newPerfTimeSeries,
  }, pageName) => {
    dispatch({
      type: ACTION_TYPE_SET_FOLDER_DATA,
      newAssets,
      newFolders,
      newPerfTimeSeries,
      pageName,
      shouldFlush: true,
      logger,
    });
  }, [logger]);

  // replace assets only, and keep other data unchanged
  const setAssetsOnly = useCallback((newAssets) => {
    dispatch({
      type: ACTION_TYPE_SET_FOLDER_DATA,
      newAssets,
      newFolders: folderData.folders,
      newPerfTimeSeries: folderData.perfTimeSeriesData,
      pageName: folderData.pageName,
      shouldFlush: true,
      logger,
    });
  }, [folderData.pageName, folderData.folders, folderData.perfTimeSeriesData, logger]);

  const appendFolderData = useCallback(({ newAssets, newFolders }) => {
    dispatch({
      type: ACTION_TYPE_SET_FOLDER_DATA,
      newAssets,
      newFolders,
      shouldFlush: false,
      logger,
    });
  }, [logger]);

  const setPerfTimeSeriesData = useCallback(({ newPerfTimeSeries }) => {
    dispatch({
      type: ACTION_TYPE_SET_PERF_TIME_SERIES,
      newPerfTimeSeries,
      logger,
    });
  }, [logger]);

  const resetFolderData = useCallback(() => {
    dispatch({ type: ACTION_TYPE_RESET_DATA });
  }, []);

  return {
    folderData,
    setFolderData,
    setAssetsOnly,
    appendFolderData,
    setPerfTimeSeriesData,
    resetFolderData,
  };
};
